import { FC } from 'react'
import styled from 'styled-components'
import { validFor } from '@/theme/validFor'
import {
  LandingPage,
  LandingPageType,
  LandingPageSectionPosition
} from '@/network/graphql.g'
import { requestThrow401 } from '@/utils/graphQl'
import { withBaseServerSideProps } from '@/HOC/withBaseServerSideProps'
import { Box, Flex } from '@/components/atoms/Grid'
import SummerHeader from '@/components/molecules/summer/header'
import SummerRecommendedSlider from '@/components/molecules/summer/bestsellerSlider'
import useTranslation from 'next-translate/useTranslation'
import Link from '@/components/atoms/link'
import { Routes } from '@/core/routes'
import { BigAccentButton } from '@/components/atoms/button'
import { H1 } from '@/components/atoms/typography'
import { getMainData } from '@/utils/mainDataHelper'
import SummerCategories from '@/components/molecules/summer/categories'
import SummerCategoriesContainer from '@/components/molecules/summer/categoriesContainer'

type SummerProps = {
  landingPage: LandingPage
}

const SummerPage: FC<SummerProps> = ({ landingPage }) => {
  const blackFridaySections = landingPage.sections.map((section) => section)
  const { t } = useTranslation('common')
  return (
    <>
      <SummerHeader landingPage={landingPage} />
      <MainWrapper flexDirection="column">
        <StyledFlex
          flexDirection={{ mobile: 'column', tablet: 'row', desktop: 'row' }}
          height={{ mobile: 'auto', tablet: '28%', desktop: '23%' }}
        >
          <SummerCategories
            blackFridaySections={blackFridaySections}
            position={LandingPageSectionPosition.TopLeft}
          />
          <SummerCategories
            blackFridaySections={blackFridaySections}
            position={LandingPageSectionPosition.TopRight}
          />
        </StyledFlex>

        <SummerCategoriesContainer
          blackFridaySections={blackFridaySections}
          position={LandingPageSectionPosition.Center1}
        />
        <Flex
          flexDirection={{
            mobile: 'column',
            tablet: 'row',
            desktop: 'row'
          }}
          height={{ mobile: '500px', tablet: '28%', desktop: '23%' }}
          m={{ mobile: 3, tablet: 4, desktop: 5 }}
        >
          <SummerMainBanner>
            <Box
              mb={{ desktop: '70px', tablet: '85px', mobile: '100px' }}
              width={{ desktop: '75%', tablet: '85%', width: '90%' }}
            >
              <MainBannerH1>{t('Summer.collection')}</MainBannerH1>
            </Box>
            <StyledLink href={Routes.campaign}>
              <StyledSummerButton title={t('SpecialLandingPage.select')} />
            </StyledLink>
          </SummerMainBanner>
        </Flex>
        <StyledSmallBanners>
          <FrogiesBanner>
            <StyledLink href={Routes.frogies}>
              <StyledSummerButton title={t('SpecialLandingPage.select')} />
            </StyledLink>
          </FrogiesBanner>
          <ForChildrenBanner>
            <StyledH1>{t('Summer.children')}</StyledH1>
            <StyledLink href={Routes.summerWithChildren}>
              <StyledSummerButton title={t('SpecialLandingPage.select')} />
            </StyledLink>
          </ForChildrenBanner>
        </StyledSmallBanners>
      </MainWrapper>
      <SummerRecommendedSlider />
    </>
  )
}

export default SummerPage

export const getServerSideProps = withBaseServerSideProps(
  async ({
    sdk,
    withMainNavigation,
    withStore
  }): Promise<GetServerSideProps<SummerProps>> => {
    const { success, response } = await requestThrow401({
      method: sdk.getBlackFridayLandingPage,
      variables: {
        type: LandingPageType.Plavky,
        withMainNavigation,
        withStore
      },
      mainResponse: (response) => response?.landingPage
    })
    if (!success) {
      return {
        notFound: true
      }
    }

    return {
      props: {
        mainData: getMainData(response),
        landingPage: response?.landingPage,
        seoTags: response?.seoTags
      }
    }
  }
)

const MainWrapper = styled(Flex)`
  width: 100%;
  background-color: ${(props) => props.theme.colors.summerMainBackground};
  ${validFor.tablet`
    height: 2100px;
  `}
  ${validFor.desktop`
    height: 2100px;
  `}
  color: ${(props) => props.theme.colors.menuBackground};
`

const StyledFlex = styled(Flex)`
  align-items: center;
  justify-content: space-around;
`

const StyledLink = styled(Link)`
  max-width: 275px;
  height: 50px;
  margin-bottom: 48px;
  ${validFor.mobile`
    margin-bottom: 20px;
    max-width: 225px;
  `}
  ${validFor.tablet`
    margin-bottom: 20px;
  `}
`

const SummerMainBanner = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  background-image: url('https://i.factcool.com/summer/landing-page-summer_11.jpg');
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: 42%;
  ${validFor.mobile`
    width:100%;
    margin-left: 0
  `}
`

const MainBannerH1 = styled(H1)`
  font-size: 48px;
  text-align: center;
  padding-bottom: 0;
  ${validFor.desktop`
    line-height: 64px;
  `}
  ${validFor.tablet`
    font-size: 36px;
    margin-top: 20px;
    margin-bottom: 0px;
    line-height: 46px
  `}
    ${validFor.mobile`
    font-size: 28px;
    margin-top:0px;
    margin-bottom: 0px;
    line-height: 42px
  `}
`

const StyledSmallBanners = styled(Flex)`
  ${validFor.desktop`
    height: 21%;
    flex-direction: row;
    margin: 0 64px 64px;
    gap: 64px;
  `}
  ${validFor.tablet`
    height: 28%;
    flex-direction: row;
    margin: 0 32px 32px;
    gap: 32px;
  `}
  ${validFor.mobile`
    height: 500px;
    flex-direction: column;
    margin: 0 16px 16px;
    gap: 16px;
  `}
`

const FrogiesBanner = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  background-image: url('https://i.factcool.com/summer/landing-page-summer_15.jpg');
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  display: flex;
  ${validFor.mobile`
    width:100%
  `}
`
const ForChildrenBanner = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  background-image: url('https://i.factcool.com/summer/landing-page-summer-17.jpg');
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  ${validFor.mobile`
    width:100%;
  `}
`

const StyledH1 = styled(H1)`
  margin-bottom: 90px;
  font-size: 48px;
  text-align: center;
  line-height: 64px;
  ${validFor.tablet`
    font-size: 36px;
    line-height: 46px;
    margin-bottom: 110px;
  `}
  ${validFor.mobile`
    font-size:28px;
    line-height:40px;
    margin-bottom: 40px;
  `}
`

const StyledSummerButton = styled(BigAccentButton)`
  background-color: ${(props) => props.theme.colors.summerBackgroundButton};
  color: ${(props) => props.theme.colors.onBackground};
  width: 100%;
  height: 100%;
  text-transform: none;
  p {
    letter-spacing: 1px;
  }
`
